/**
 * 원불교 법령 관리 시스템 - 날짜/시간 유틸리티
 * 
 * 이 파일은 날짜와 시간을 다루는 유틸리티 함수들을 제공합니다.
 * dayjs 라이브러리를 사용하여 타임존 처리, 포맷팅, 상대적 시간 표시 등을 구현합니다.
 * 
 * 주요 기능:
 * - UTC 날짜를 사용자 타임존으로 변환
 * - 다양한 날짜/시간 포맷팅
 * - 상대적 시간 표시 (예: "3시간 전")
 * - 한국어 날짜 형식 지원
 * - 날짜 검증 및 비교
 * 
 * @author 원불교 법령 관리 시스템 개발팀
 * @version 1.0.0
 */

import dayjs from 'dayjs';
import 'dayjs/locale/ko';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

// dayjs 플러그인 및 로케일 설정
dayjs.extend(utc); // UTC 지원
dayjs.extend(timezone); // 타임존 지원
dayjs.locale('ko'); // 한국어 로케일 설정

/**
 * 사용자의 현재 타임존을 가져옵니다.
 * 
 * @returns {string} 사용자의 타임존 (예: "Asia/Seoul")
 */
export const getUserTimezone = (): string => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

/**
 * UTC 날짜를 사용자 타임존으로 변환하여 지정된 형식으로 포맷팅합니다.
 * 
 * @param {string} utcDateString - UTC 날짜 문자열
 * @param {string} [format='YYYY-MM-DD HH:mm:ss'] - 출력 형식
 * @returns {string} 포맷팅된 날짜 문자열
 */
export const formatDateTime = (
  utcDateString: string,
  format: string = 'YYYY-MM-DD HH:mm:ss'
): string => {
  if (!utcDateString) return '';
  
  const userTimezone = getUserTimezone();
  return dayjs.utc(utcDateString).tz(userTimezone).format(format);
};

/**
 * 상대적 시간을 표시합니다 (예: "3시간 전", "2일 전").
 * 
 * @param {string} utcDateString - UTC 날짜 문자열
 * @returns {string} 상대적 시간 문자열
 */
export const formatRelativeTime = (utcDateString: string): string => {
  if (!utcDateString) return '';
  
  const userTimezone = getUserTimezone();
  const date = dayjs.utc(utcDateString).tz(userTimezone);
  const now = dayjs();
  
  // 시간 차이 계산
  const diffMinutes = now.diff(date, 'minute');
  const diffHours = now.diff(date, 'hour');
  const diffDays = now.diff(date, 'day');
  const diffWeeks = now.diff(date, 'week');
  const diffMonths = now.diff(date, 'month');
  const diffYears = now.diff(date, 'year');
  
  // 상대적 시간 표시 로직
  if (diffMinutes < 1) return '방금 전';
  if (diffMinutes < 60) return `${diffMinutes}분 전`;
  if (diffHours < 24) return `${diffHours}시간 전`;
  if (diffDays < 7) return `${diffDays}일 전`;
  if (diffWeeks < 4) return `${diffWeeks}주 전`;
  if (diffMonths < 12) return `${diffMonths}개월 전`;
  return `${diffYears}년 전`;
};

// 날짜만 표시
export const formatDate = (utcDateString: string): string => {
  return formatDateTime(utcDateString, 'YYYY-MM-DD');
};

// 시간만 표시
export const formatTime = (utcDateString: string): string => {
  return formatDateTime(utcDateString, 'HH:mm:ss');
};

// 한국어 형식으로 표시
export const formatKoreanDateTime = (utcDateString: string): string => {
  return formatDateTime(utcDateString, 'YYYY년 MM월 DD일 HH:mm');
};

// 한국어 날짜만 표시
export const formatKoreanDate = (utcDateString: string): string => {
  return formatDateTime(utcDateString, 'YYYY년 MM월 DD일');
};

// 요일 포함 표시
export const formatDateWithDay = (utcDateString: string): string => {
  return formatDateTime(utcDateString, 'YYYY-MM-DD (ddd)');
};

// 현재 시간을 UTC로 반환
export const getCurrentUTCTime = (): string => {
  return dayjs().utc().format();
};

// 날짜 범위 검증
export const isValidDateRange = (startDate: string, endDate: string): boolean => {
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  return start.isValid() && end.isValid() && start.isBefore(end);
};

// 날짜 비교 (같은 날인지 확인)
export const isSameDay = (date1: string, date2: string): boolean => {
  const userTimezone = getUserTimezone();
  const d1 = dayjs.utc(date1).tz(userTimezone);
  const d2 = dayjs.utc(date2).tz(userTimezone);
  return d1.isSame(d2, 'day');
};

// 결의일자용 함수 (타임존 변환 없이 날짜만 처리)
export const formatResolutionDate = (dateString: string): string => {
  if (!dateString) return '';
  
  // 날짜 문자열을 직접 파싱하여 한국어 형식으로 변환
  const date = dayjs(dateString);
  if (!date.isValid()) return '';
  
  return date.format('YYYY년 MM월 DD일');
};

// 결의일자용 함수 (YYYY-MM-DD 형식)
export const formatResolutionDateSimple = (dateString: string): string => {
  if (!dateString) return '';
  
  // 날짜 문자열을 직접 파싱
  const date = dayjs(dateString);
  if (!date.isValid()) return '';
  
  return date.format('YYYY-MM-DD');
};

// 간단한 날짜시간 형식 (YYYY-MM-DD HH:mm)
export const formatSimpleDateTime = (utcDateString: string): string => {
  return formatDateTime(utcDateString, 'YYYY-MM-DD HH:mm');
};

// 언어에 따른 날짜시간 형식
export const formatLocalizedDateTime = (utcDateString: string, language: string = 'ko'): string => {
  if (!utcDateString) return '';
  
  const userTimezone = getUserTimezone();
  const date = dayjs.utc(utcDateString).tz(userTimezone);
  
  if (language === 'en') {
    return date.format('YYYY-MM-DD HH:mm');
  } else {
    return date.format('YYYY년 MM월 DD일 HH:mm');
  }
};

// 언어에 따른 날짜 형식
export const formatLocalizedDate = (utcDateString: string, language?: string): string => {
  if (!utcDateString) return '';
  
  // 언어가 지정되지 않은 경우 현재 언어를 감지
  let currentLanguage = language;
  if (!currentLanguage) {
    try {
      // localStorage에서 언어 설정 확인
      const storedLanguage = localStorage.getItem('i18nextLng');
      if (storedLanguage) {
        currentLanguage = storedLanguage;
      } else {
        // 브라우저 언어 감지
        const browserLanguage = navigator.language.split('-')[0];
        currentLanguage = browserLanguage === 'en' ? 'en' : 'ko';
      }
    } catch (error) {
      currentLanguage = 'ko'; // 기본값
    }
  }
  
  const userTimezone = getUserTimezone();
  const date = dayjs.utc(utcDateString).tz(userTimezone);
  
  if (currentLanguage === 'en') {
    return date.format('YYYY-MM-DD');
  } else {
    return date.format('YYYY년 MM월 DD일');
  }
}; 